import { useState } from "react";
import axios from "axios";
import { FileDown, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PdfDownloadCard = ({ validationId }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isSupporter = user && (user.is_premium || user.role === "admin");

  if (!validationId) return null;

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/validations/${validationId}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : "facture_apercu.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const txt = JSON.parse(await err.response.data.text());
          setError(txt.detail || "La génération du PDF a échoué.");
        } catch {
          setError("La génération du PDF a échoué.");
        }
      } else {
        setError(err.response?.data?.detail || "La génération du PDF a échoué.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isSupporter) {
    return (
      <Card className="shadow-lg border-emerald-100 bg-emerald-50/40" data-testid="pdf-locked-card">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg mt-0.5">
              <FileDown className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                Nouveau — Aperçu PDF lisible de votre facture
                <span className="text-[10px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.5 rounded">Supporters</span>
              </p>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Transformez ce XML en un PDF clair et lisible : fournisseur, client, lignes, TVA et totaux —
                idéal pour vérifier visuellement la facture ou l'archiver. Cette fonctionnalité a un coût
                d'hébergement et de développement : votre soutien de 2 € la finance et débloque aussi le
                contrôle IA, la vérification annuaire et 1000 validations/mois.
              </p>
              <a
                href="https://paypal.me/LevasseurQuentin/2EUR"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pdf-locked-support-link"
                className="inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Heart className="h-3.5 w-3.5" />
                Devenir supporter — 2 €
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-emerald-200" data-testid="pdf-download-card">
      <CardContent className="py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg mt-0.5">
              <FileDown className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                Aperçu PDF lisible
                <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
              </p>
              <p className="text-xs text-slate-500">
                Générez un PDF clair de cette facture : parties, lignes, TVA et totaux.
              </p>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            disabled={loading}
            data-testid="pdf-download-button"
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl gap-2"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Télécharger le PDF
              </>
            )}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600" data-testid="pdf-download-error">{error}</p>}
      </CardContent>
    </Card>
  );
};
